import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Download, Phone, Copy } from "lucide-react";

const STATUSES = ["new", "shortlisted", "rejected", "hired"];

export function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const load = useCallback(() => api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/applications/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try { await api.delete(`/admin/applications/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const download = async (a) => {
    try {
      const r = await api.get(`/admin/applications/${a.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.resume.filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download resume"); }
  };
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join-team`);
    toast.success("Hiring link copied");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{apps.length} application{apps.length === 1 ? "" : "s"}</p>
        <button data-testid="copy-hiring-link" onClick={copyLink} className="text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-1" style={{ background: "var(--yellow)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}>
          <Copy size={12} /> Copy hiring link
        </button>
      </div>
      {apps.length === 0 && <div className="card-soft p-8 text-center text-sm" style={{ color: "var(--text-secondary)" }}>No applications yet. Share the hiring link to start receiving them.</div>}
      <div className="grid md:grid-cols-2 gap-4">
        {apps.map((a) => <AppCard key={a.id} a={a} onStatus={setStatus} onRemove={remove} onDownload={download} />)}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-3 text-sm"><span style={{ color: "var(--text-secondary)" }}>{k}</span><span className="font-semibold text-right">{v || "—"}</span></div>;
}

function AppCard({ a, onStatus, onRemove, onDownload }) {
  const money = (n) => `${a.currency} ${Number(n).toLocaleString()}`;
  return (
    <div data-testid={`application-${a.id}`} className="card-soft p-5 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-xl" style={{ color: "var(--primary)" }}>{a.name}</div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{a.email} · {new Date(a.created_at).toLocaleDateString()}</div>
        </div>
        <select data-testid={`application-status-${a.id}`} value={a.status} onChange={(e) => onStatus(a.id, e.target.value)} className="text-xs font-bold rounded-full border px-2 py-1 capitalize bg-transparent" style={{ borderColor: "var(--border-strong)" }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Row k="Qualification" v={a.qualification} />
      <Row k="Experience" v={`${a.experience_years} yrs`} />
      <Row k="City" v={a.city} />
      <Row k="Languages" v={(a.languages || []).join(", ")} />
      <Row k="Notice / joining" v={[a.notice_period, a.joining_date].filter(Boolean).join(" · ")} />
      <Row k="Expected CTC (yearly)" v={money(a.expected_ctc)} />
      <Row k="Expected salary (monthly)" v={money(a.expected_salary)} />
      <div className="flex items-center gap-2 pt-2 flex-wrap">
        <a href={`tel:${a.phone}`} className="text-xs font-bold inline-flex items-center gap-1" style={{ color: "var(--primary)" }}><Phone size={12} />{a.phone}</a>
        <div className="flex-1" />
        {a.resume && <button data-testid={`application-resume-${a.id}`} onClick={() => onDownload(a)} className="text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1" style={{ border: "1.5px solid var(--primary)", color: "var(--primary)" }}><Download size={12} />Resume</button>}
        <button data-testid={`application-del-${a.id}`} onClick={() => onRemove(a.id)} className="text-red-600"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}
